import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaFileContract, FaPlus, FaSyncAlt, FaUserShield } from 'react-icons/fa';
import { POLICY_STATUS_ORDER } from '../constants';
import { usePasStore } from '../pasStore';
import type { Policy, PolicyStatus } from '../types';
import { daysBetween, formatCurrency, formatDate, relativeTime, todayStr } from '../utils';
import { Button } from '../components/Button';
import { NewPolicyModal } from '../components/NewPolicyModal';
import { PolicyStatusBadge } from '../components/StatusBadges';
import { Card, PageHeader, SectionCard, StatCard } from '../components/primitives';

const PIPELINE: PolicyStatus[] = ['draft', 'pending_underwriting', 'issued', 'active'];

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const policies = usePasStore((s) => s.policies);
  const [newOpen, setNewOpen] = useState(false);

  const stats = useMemo(() => {
    const byStatus = Object.fromEntries(POLICY_STATUS_ORDER.map((s) => [s, 0])) as Record<PolicyStatus, number>;
    let inForcePremium = 0;
    policies.forEach((p) => {
      byStatus[p.status] += 1;
      if (p.status === 'active') inForcePremium += p.annualPremium;
    });
    const maxPipeline = Math.max(1, ...PIPELINE.map((s) => byStatus[s]));
    return { byStatus, inForcePremium, maxPipeline };
  }, [policies]);

  const upcomingRenewals = useMemo(
    () =>
      policies
        .filter((p) => (p.status === 'active' || p.status === 'issued') && daysBetween(todayStr(), p.expirationDate) <= 120)
        .sort((a, b) => a.expirationDate.localeCompare(b.expirationDate))
        .slice(0, 5),
    [policies],
  );

  const pendingUw = useMemo(
    () => policies.filter((p) => p.status === 'pending_underwriting').sort((a, b) => a.underwriting.priority.localeCompare(b.underwriting.priority)).slice(0, 5),
    [policies],
  );

  const recentActivity = useMemo(() => {
    const events = policies.flatMap((p) =>
      p.timeline.slice(0, 3).map((e) => ({ event: e, policy: p })),
    );
    return events.sort((a, b) => new Date(b.event.timestamp).getTime() - new Date(a.event.timestamp).getTime()).slice(0, 7);
  }, [policies]);

  const open = (p: Policy) => navigate(`/policy-admin/policies/${p.id}`);

  return (
    <div>
      <PageHeader
        eyebrow="Policy Administration"
        title="Dashboard"
        subtitle="Monitor the full policy lifecycle — from approved quote to active coverage."
        actions={
          <Button leftIcon={<FaPlus className="h-3 w-3" />} onClick={() => setNewOpen(true)}>
            New Policy
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total policies" value={policies.length} icon={<FaFileContract />} accent="primary" sublabel="Across all statuses" />
        <StatCard label="Active in force" value={stats.byStatus.active} icon={<FaCheckCircle />} accent="success" sublabel={`${formatCurrency(stats.inForcePremium)} premium`} />
        <StatCard label="In underwriting" value={stats.byStatus.pending_underwriting} icon={<FaUserShield />} accent="warning" sublabel="Awaiting decision" />
        <StatCard label="Renewals ≤120d" value={upcomingRenewals.length} icon={<FaSyncAlt />} accent="info" sublabel="Approaching expiration" />
      </div>

      <Card className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Workflow pipeline
          </h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/policy-admin/policies')}>
            View all policies
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {PIPELINE.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => navigate(`/policy-admin/policies?status=${status}`)}
              className="rounded-lg border p-4 text-left transition-colors hover:bg-[var(--color-hover-background)]"
              style={{ borderColor: 'var(--color-border-light)' }}
            >
              <p className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {stats.byStatus[status]}
              </p>
              <div className="mt-2">
                <PolicyStatusBadge status={status} />
              </div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-hover-background)' }}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${(stats.byStatus[status] / stats.maxPipeline) * 100}%`, backgroundColor: 'var(--color-primary-500)' }}
                />
              </div>
            </button>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard
          title="Underwriting queue"
          description="Highest priority submissions awaiting a decision"
          className="lg:col-span-1"
          bodyClassName="p-0"
          actions={
            <Button variant="ghost" size="sm" onClick={() => navigate('/policy-admin/underwriting')}>
              Open queue
            </Button>
          }
        >
          {pendingUw.length === 0 ? (
            <p className="p-5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              The underwriting queue is clear. 🎉
            </p>
          ) : (
            <ul>
              {pendingUw.map((p) => (
                <li key={p.id} className="border-b last:border-b-0" style={{ borderColor: 'var(--color-border-light)' }}>
                  <button type="button" onClick={() => open(p)} className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition-colors hover:bg-[var(--color-hover-background)]">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {p.insured.name}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        {p.policyNumber} · {formatCurrency(p.annualPremium)}
                      </p>
                    </div>
                    <span
                      className="flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize"
                      style={{
                        backgroundColor: p.underwriting.priority === 'high' ? 'var(--color-error-100)' : 'var(--color-hover-background)',
                        color: p.underwriting.priority === 'high' ? 'var(--color-error-800)' : 'var(--color-text-secondary)',
                      }}
                    >
                      {p.underwriting.priority}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Recent activity" description="Latest events across the book" className="lg:col-span-1" bodyClassName="p-0">
          <ul>
            {recentActivity.map(({ event, policy }) => (
              <li key={event.id} className="border-b last:border-b-0" style={{ borderColor: 'var(--color-border-light)' }}>
                <button type="button" onClick={() => open(policy)} className="flex w-full flex-col gap-0.5 px-5 py-3 text-left transition-colors hover:bg-[var(--color-hover-background)]">
                  <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {event.title}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {policy.policyNumber} · {event.actor} · {relativeTime(event.timestamp)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title="Upcoming renewals" description="Policies approaching expiration" className="lg:col-span-1" bodyClassName="p-0">
          {upcomingRenewals.length === 0 ? (
            <p className="p-5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              No renewals in the next 120 days.
            </p>
          ) : (
            <ul>
              {upcomingRenewals.map((p) => (
                <li key={p.id} className="border-b last:border-b-0" style={{ borderColor: 'var(--color-border-light)' }}>
                  <button type="button" onClick={() => open(p)} className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition-colors hover:bg-[var(--color-hover-background)]">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {p.insured.name}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        Expires {formatDate(p.expirationDate)}
                      </p>
                    </div>
                    <span className="flex-shrink-0 text-xs font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                      {daysBetween(todayStr(), p.expirationDate)}d
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <NewPolicyModal isOpen={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
};
